import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Chip } from '../../components/ui/Card';
import { Modal } from '../albums/Modal';
import { ApiError, api } from '../../lib/api';
import { Select } from '../../components/ui/form';

export const ROLES = [
  'artist',
  'feature',
  'producer',
  'remixer',
  'vocalist',
  'writer',
  'mix',
  'master',
] as const;

export type CreditRole = (typeof ROLES)[number];

export interface Credit {
  id: number;
  role: CreditRole;
  position: number;
  name: string;
  username: string | null;
  user_id: number | null;
  /** Only present to the track's owner and the credited person. */
  status?: 'pending' | 'accepted' | 'declined';
}

interface Draft {
  /** An account, if this credit names one. */
  username: string;
  /** A plain name, if it does not. */
  name: string;
  role: CreditRole;
  status?: Credit['status'];
}

const MAX = 20;

/**
 * Edit a track's credits.
 *
 * ## Naming an account asks them, it does not credit them
 *
 * Said in the UI as plainly as it is enforced on the server, because it is the
 * behaviour people will otherwise report as a bug: you add a producer, save,
 * and their name does not appear on the public page. It has not failed — they
 * have not answered yet.
 *
 * The reason is worth knowing: the feed surfaces a track to everybody who
 * follows a credited user, so an unconfirmed credit would be a way to put your
 * upload in front of somebody else's entire audience.
 */
export function CreditsEditor({
  trackId,
  credits,
  onClose,
  onSaved,
}: {
  trackId: number;
  credits: Credit[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [rows, setRows] = useState<Draft[]>(() =>
    credits.map((credit) => ({
      username: credit.username ?? '',
      name: credit.username ? '' : credit.name,
      role: credit.role,
      status: credit.status,
    })),
  );

  const save = useMutation({
    mutationFn: () =>
      api.put(`/tracks/${trackId}/credits`, {
        credits: rows
          // A row somebody started and left blank is not an error, it is a
          // row they changed their mind about.
          .filter((row) => row.username.trim() !== '' || row.name.trim() !== '')
          .map((row) => ({
            username: row.username.trim() || undefined,
            name: row.name.trim() || undefined,
            role: row.role,
          })),
      }),
    onSuccess: onSaved,
  });

  const update = (index: number, patch: Partial<Draft>) =>
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  const fieldClasses = [
    'rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 text-sm text-ink',
    'focus:border-line-strong placeholder:text-faint',
  ].join(' ');

  // `fieldErrors` rather than `details`: the API client already pulls the
  // validator's per-field map out of the envelope, which is why it exists.
  const fieldErrors: Record<string, string> =
    save.error instanceof ApiError ? save.error.fieldErrors : {};

  return (
    <Modal title="Credits" onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4" data-credits-form>
        <p className="text-sm text-soft">
          Everyone who worked on this. Use a{' '}
          <span className="font-medium text-ink">@username</span> to link somebody on{' '}
          {location.hostname.replace(/^www\./, '')}, or just type a name for anyone who is not here.
        </p>

        {rows.length === 0 && (
          <p className="rounded-[var(--radius-control)] border border-dashed border-line-strong px-4 py-6 text-center text-sm text-soft">
            No credits yet.
          </p>
        )}

        <ul className="flex flex-col gap-3" data-credit-rows>
          {rows.map((row, index) => {
            const error =
              fieldErrors[`credits.${index}.username`] ??
              fieldErrors[`credits.${index}.name`] ??
              fieldErrors[`credits.${index}`];

            return (
              <li key={index} className="flex flex-col gap-1.5" data-credit-row={index}>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    className={`${fieldClasses} min-w-0 flex-1`}
                    placeholder="@username or a name"
                    aria-label={`Credit ${index + 1}`}
                    data-credit-who={index}
                    value={row.username || row.name}
                    disabled={save.isPending}
                    onChange={(event) => {
                      const value = event.target.value;

                      // A leading @ is how somebody says "this is an account".
                      // Splitting on it here means the server is told which of
                      // the two kinds of credit this is, rather than guessing.
                      update(index, value.startsWith('@')
                        ? { username: value.slice(1), name: '' }
                        : { username: '', name: value });
                    }}
                  />

                  <Select
                    aria-label={`Role for credit ${index + 1}`}
                    data-credit-role={index}
                    value={row.role}
                    disabled={save.isPending}
                    options={ROLES.map((role) => ({
                      value: role,
                      label: label(role),
                    }))}
                    onChange={(next) => update(index, { role: next as CreditRole })}
                  />

                  <button
                    type="button"
                    aria-label={`Remove credit ${index + 1}`}
                    disabled={save.isPending}
                    onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-pill)] text-soft transition-colors hover:bg-raised hover:text-hot"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                      <path d="M3 7h10v2H3z" />
                    </svg>
                  </button>
                </div>

                {row.status && row.status !== 'accepted' && (
                  <span className="flex items-center gap-2">
                    <Chip tone={row.status === 'pending' ? 'default' : 'hot'}>
                      {row.status === 'pending' ? 'Waiting for them' : 'Declined'}
                    </Chip>
                    <span className="text-xs text-faint">
                      {row.status === 'pending'
                        ? 'Not shown publicly until they accept.'
                        : 'They declined, so this is not shown.'}
                    </span>
                  </span>
                )}

                {error && <p className="text-xs text-negative">{error}</p>}
              </li>
            );
          })}
        </ul>

        {rows.length < MAX && (
          <div>
            <Button
              variant="secondary"
              disabled={save.isPending}
              data-add-credit
              onClick={() => setRows((current) => [...current, { username: '', name: '', role: 'artist' }])}
            >
              Add a credit
            </Button>
          </div>
        )}

        {save.isError && Object.keys(fieldErrors).length === 0 && (
          <Alert tone="error">
            {save.error instanceof ApiError ? save.error.message : 'That could not be saved.'}
          </Alert>
        )}

        <p className="text-xs text-faint">
          Crediting an account sends them a request. Their name appears once they accept it — which
          is what stops anyone attaching a well-known artist to their own upload.
        </p>

        <div className="flex items-center gap-3 border-t border-line pt-4">
          <Button type="submit" loading={save.isPending}>
            Save credits
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function label(role: CreditRole): string {
  // `feature` reads as "Featuring" and `mix`/`master` as verbs-turned-nouns;
  // the rest are already the words people use.
  const words: Record<CreditRole, string> = {
    artist: 'Artist',
    feature: 'Featuring',
    producer: 'Producer',
    remixer: 'Remixer',
    vocalist: 'Vocals',
    writer: 'Writer',
    mix: 'Mixed by',
    master: 'Mastered by',
  };

  return words[role];
}
